import type { ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@voll-intelligence/ui-kit/ui/avatar';
import { personInitials, shortPersonName } from './person_name.js';

export type PersonItem = {
  readonly id: string;
  /** O nome CRU como chega do roster/do agente — a linha formata ao renderizar. */
  readonly name: string;
  /** A segunda linha (e-mail, matrícula, departamento), quando o host a tem. */
  readonly description?: string;
  /** A foto do perfil (#2640): URL pré-assinada de vida curta. `null`/ausente ⇒
   *  iniciais — e a URL que expirar ou falhar também cai nas iniciais, pelo
   *  próprio Avatar do DS. */
  readonly photoUrl?: string | null;
};

/**
 * A linha de pessoa que toda lista/gatilho do produto mostra: retrato com as
 * iniciais + nome curto ("Nome Sobrenome") + legenda. O nome inteiro vai no
 * `title` para quem passa o mouse e no nome acessível.
 *
 * `size`: `sm` é a linha de lista do DS (retrato 24); `xs` é o retrato de 20 que
 * cabe numa pílula de 36 (gatilho do campo, vaga do quarto).
 */
export function PersonAvatar({
  name,
  photoUrl,
  size = 'sm',
  selected = false,
  locale,
}: {
  readonly name: string;
  readonly photoUrl?: string | null;
  readonly size?: 'sm' | 'xs';
  /** O retrato "acende" (tinta sobre o fundo) quando a pessoa está escolhida —
   *  a mesma leitura da pilha do gatilho, com transição curta de cor. */
  readonly selected?: boolean;
  readonly locale?: string;
}): ReactNode {
  return (
    <Avatar size="sm" className={size === 'xs' ? 'size-5' : undefined} aria-hidden="true">
      {photoUrl && <AvatarImage src={photoUrl} alt="" />}
      <AvatarFallback
        className={`font-medium transition-colors duration-150 ${size === 'xs' ? 'text-[9px]' : ''} ${
          selected ? 'bg-primary text-primary-foreground' : ''
        }`}
      >
        {personInitials(name, locale)}
      </AvatarFallback>
    </Avatar>
  );
}

export function PersonRow({
  person,
  displayName,
  description,
  locale,
  size = 'sm',
  selected = false,
}: {
  readonly person: PersonItem;
  /** Substitui o nome curto — a linha "Você". */
  readonly displayName?: string;
  /** Substitui a legenda — o motivo de a linha estar desabilitada, por exemplo. */
  readonly description?: string;
  readonly locale?: string;
  readonly size?: 'sm' | 'xs';
  readonly selected?: boolean;
}): ReactNode {
  const full = person.name.trim();
  const short = displayName ?? shortPersonName(full, locale);
  const caption = description ?? person.description;
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <PersonAvatar
        name={full}
        photoUrl={person.photoUrl}
        size={size}
        selected={selected}
        locale={locale}
      />
      <span className="grid min-w-0 flex-1 leading-tight" title={full}>
        <span className="text-ui truncate text-foreground">{short}</span>
        {caption && <span className="text-caption truncate text-muted-foreground">{caption}</span>}
      </span>
    </span>
  );
}
